import type { EditRequestFormData } from "@nlc/shared-types";

import { Controller } from "react-hook-form";

import type { BadgeVariant } from "@/components/badge";

import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { ChevronDownIcon } from "@/components/icons.tsx";
import {
  Select,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from "@/components/select";
import { useEditRequestFormContext } from "@/features/request/edit/hooks/useEditRequestForm";
import { useFieldUpdate } from "@/features/request/edit/hooks/useFieldUpdate";

export interface BadgeOption {
  id: number;
  label: string;
  variant: BadgeVariant;
}

export function SelectWithBadges(props: {
  name: keyof EditRequestFormData;
  options: BadgeOption[] | readonly BadgeOption[];
}) {
  const methods = useEditRequestFormContext();
  const { updateField } = useFieldUpdate();

  return (
    <Controller
      control={methods.control}
      name={props.name}
      render={({
        field: { name, onBlur, onChange, value },
        fieldState: { invalid },
      }) => (
        <Select
          aria-label="Select"
          className="h-[22px] whitespace-nowrap"
          isInvalid={invalid}
          name={name}
          onBlur={onBlur}
          onSelectionChange={(value) => {
            onChange(value);
            updateField(name, value);
          }}
          selectedKey={value?.toString() ?? ""}
        >
          <Button
            className="flex items-center justify-center outline-none"
            variant="unstyled"
          >
            <Badge
              isRounded
              size="small"
              variant={
                props.options.find((opt) => opt.id === value)?.variant ??
                "primary"
              }
            >
              {props.options.find((opt) => opt.id === value)?.label ?? "Select"}
              <Badge.Icon>
                <ChevronDownIcon />
              </Badge.Icon>
            </Badge>
          </Button>
          <SelectPopover className="w-fit" placement="bottom start">
            <SelectListBox>
              {props.options.map(({ id, label, variant }) => (
                <SelectListItem id={id} key={id} textValue={label}>
                  <Badge isRounded size="small" variant={variant}>
                    {label}
                  </Badge>
                </SelectListItem>
              ))}
            </SelectListBox>
          </SelectPopover>
        </Select>
      )}
    />
  );
}
